import { Link, useLocation } from "react-router";
import { getTranslationFn } from "../../i18n";
import { getLocale, type Locale, setPreferredLocale } from "../../lib/locale";
import { cn } from "../../lib/utils";
import * as Paths from "../../paths";

interface MarketingFooterProps {
  locale?: Locale;
}

function getLocalizedMarketingPath(pathname: string, locale: Locale): string {
  const normalizedPath =
    pathname.length > 1 && pathname.endsWith("/")
      ? pathname.slice(0, -1)
      : pathname;

  if (normalizedPath === "/docs" || normalizedPath.startsWith("/docs/")) {
    return normalizedPath;
  }

  switch (normalizedPath) {
    case "/changelog":
      return Paths.changelog();
    case "/":
    case "/en":
      return locale === "en" ? "/en" : "/";
    case "/about":
    case "/en/about":
      return locale === "en" ? "/en/about" : "/about";
    case "/pricing":
    case "/en/pricing":
      return locale === "en" ? "/en/pricing" : "/pricing";
    case "/security":
    case "/en/security":
      return locale === "en" ? "/en/security" : "/security";
    default:
      return locale === "en" ? "/en" : "/";
  }
}

export function MarketingFooter({ locale: localeProp }: MarketingFooterProps) {
  const location = useLocation();
  const locale = localeProp ?? getLocale();
  const t = getTranslationFn(locale);
  const isLandingPage =
    location.pathname === "/" || location.pathname === "/en";
  const landingBase = locale === "en" ? "/en" : "/";
  const anchorPrefix = isLandingPage ? "" : landingBase;
  const pricingPath = locale === "en" ? "/en/pricing" : Paths.pricing();
  const securityPath = locale === "en" ? "/en/security" : Paths.security();
  const aboutPath = locale === "en" ? "/en/about" : Paths.about();
  const englishPath = getLocalizedMarketingPath(location.pathname, "en");
  const polishPath = getLocalizedMarketingPath(location.pathname, "pl");

  const handleLocaleChange = (nextLocale: Locale) => {
    setPreferredLocale(nextLocale);
  };

  return (
    <footer className="bg-white border-t border-slate-100 py-12">
      <div className="container mx-auto px-4">
        <div className="grid md:grid-cols-5 gap-12 mb-12">
          <div className="col-span-1">
            <img src="/assets/logo.svg" alt="Logo" className="w-32 mb-6" />
            <p className="text-slate-500 text-sm leading-relaxed">
              {t("Landing.footer.tagline")}
            </p>
          </div>
          <div>
            <h4 className="font-semibold text-slate-900 mb-4 text-sm">
              {t("Landing.footer.product")}
            </h4>
            <ul className="space-y-2.5 text-sm text-slate-500">
              <li>
                <Link
                  to={`${anchorPrefix}#use-cases`}
                  className="hover:text-slate-900 transition-colors"
                >
                  {t("Landing.nav.howItWorks")}
                </Link>
              </li>
              <li>
                <Link
                  to={pricingPath}
                  className="hover:text-slate-900 transition-colors"
                >
                  {t("Landing.nav.pricing")}
                </Link>
              </li>
              <li>
                <Link
                  to={securityPath}
                  className="hover:text-slate-900 transition-colors"
                >
                  {t("Landing.nav.security")}
                </Link>
              </li>
            </ul>
          </div>
          <div>
            <h4 className="font-semibold text-slate-900 mb-4 text-sm">
              {t("Landing.footer.resources")}
            </h4>
            <ul className="space-y-2.5 text-sm text-slate-500">
              <li>
                <Link
                  to={Paths.docs()}
                  className="hover:text-slate-900 transition-colors"
                >
                  {t("Landing.footer.docs")}
                </Link>
              </li>
              <li>
                <Link
                  to={Paths.changelog()}
                  className="hover:text-slate-900 transition-colors"
                >
                  {t("Landing.footer.changelog")}
                </Link>
              </li>
              <li>
                <Link
                  to={Paths.eurekaSeoIndex()}
                  className="hover:text-slate-900 transition-colors"
                >
                  {t("Landing.footer.taxInterpretations")}
                </Link>
              </li>
            </ul>
          </div>
          <div>
            <h4 className="font-semibold text-slate-900 mb-4 text-sm">
              {t("Landing.footer.company")}
            </h4>
            <ul className="space-y-2.5 text-sm text-slate-500">
              <li>
                <Link
                  to={aboutPath}
                  className="hover:text-slate-900 transition-colors"
                >
                  {t("Landing.footer.aboutUs")}
                </Link>
              </li>
              <li>
                <Link
                  to="mailto:hello@anylawyer.com"
                  className="hover:text-slate-900 transition-colors"
                >
                  {t("Landing.footer.contactUs")}
                </Link>
              </li>
            </ul>
          </div>
          <div>
            <h4 className="font-semibold text-slate-900 mb-4 text-sm">
              {t("Landing.footer.legal")}
            </h4>
            <ul className="space-y-2.5 text-sm text-slate-500">
              <li>
                <Link
                  to={Paths.privacyPolicy()}
                  className="hover:text-slate-900 transition-colors"
                >
                  {t("Landing.footer.privacyPolicy")}
                </Link>
              </li>
              <li>
                <Link
                  to={Paths.termsOfService()}
                  className="hover:text-slate-900 transition-colors"
                >
                  {t("Landing.footer.termsOfService")}
                </Link>
              </li>
            </ul>
          </div>
        </div>
        <div className="border-t border-slate-100 pt-8 flex flex-col items-center justify-between gap-4 md:flex-row">
          <p className="text-sm text-slate-400">
            {t("Landing.footer.copyright")}
          </p>
          <div className="flex items-center gap-1 text-sm">
            <Link
              to={englishPath}
              onClick={() => handleLocaleChange("en")}
              className={cn(
                "transition-colors",
                locale === "en"
                  ? "font-semibold text-slate-900"
                  : "text-slate-400 hover:text-slate-600"
              )}
            >
              EN
            </Link>
            <span className="text-slate-300">/</span>
            <Link
              to={polishPath}
              onClick={() => handleLocaleChange("pl")}
              className={cn(
                "transition-colors",
                locale === "pl"
                  ? "font-semibold text-slate-900"
                  : "text-slate-400 hover:text-slate-600"
              )}
            >
              PL
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
